import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Box,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  IconButton,
  Collapse,
  Paper
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  PlayArrow as RunIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Undo as RollbackIcon,
  BugReport as TestIcon
} from '@material-ui/icons';
import axios from 'axios';

const SiteReviewMigration = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [message, setMessage] = useState(null);
  const [expandDetails, setExpandDetails] = useState(false);

  // Fetch current migration status
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/migrations/review-structure/status');
      setStatus(response.data.status);
      setMessage(null);
    } catch (error) {
      console.error('Error fetching status:', error);
      setMessage({
        type: 'error',
        text: 'Failed to fetch migration status'
      });
    } finally {
      setLoading(false);
    }
  };

  // Test migration without changes
  const testMigration = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/migrations/review-structure/test');
      setTestResults(response.data.tests);
      setMessage({
        type: 'info',
        text: 'Migration test completed'
      });
    } catch (error) {
      console.error('Error testing migration:', error);
      setMessage({
        type: 'error',
        text: 'Failed to test migration'
      });
    } finally {
      setLoading(false);
    }
  };

  // Run the actual migration
  const runMigration = async () => {
    setRunning(true);
    setShowConfirmDialog(false);
    try {
      const response = await axios.post('/api/admin/migrations/review-structure/run');

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Migration completed! ${response.data.stats.migratedReviews} reviews migrated.`
        });

        // Refresh status
        await fetchStatus();
      } else {
        setMessage({
          type: 'warning',
          text: response.data.message || 'Migration completed with warnings'
        });
      }
    } catch (error) {
      console.error('Error running migration:', error);
      setMessage({
        type: 'error',
        text: 'Failed to run migration: ' + (error.response?.data?.error || error.message)
      });
    } finally {
      setRunning(false);
    }
  };

  // Rollback migration
  const rollbackMigration = async () => {
    setRunning(true);
    setShowConfirmDialog(false);
    try {
      const response = await axios.post('/api/admin/migrations/review-structure/rollback');

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Rollback completed! ${response.data.stats.rolledBack} reviews rolled back.`
        });

        // Refresh status
        await fetchStatus();
      }
    } catch (error) {
      console.error('Error rolling back:', error);
      setMessage({
        type: 'error',
        text: 'Failed to rollback: ' + (error.response?.data?.error || error.message)
      });
    } finally {
      setRunning(false);
    }
  };

  // Load status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const handleAction = (type) => {
    setActionType(type);
    setShowConfirmDialog(true);
  };

  const confirmAction = () => {
    switch (actionType) {
      case 'run':
        runMigration();
        break;
      case 'rollback':
        rollbackMigration();
        break;
      default:
        break;
    }
  };

  const getStatusColor = () => {
    if (!status) return 'default';
    if (status.migrationComplete) return 'success';
    if (status.unmigratedReviews > 0) return 'warning';
    return 'default';
  };

  const getStatusIcon = () => {
    if (!status) return null;
    if (status.migrationComplete) return <CheckIcon />;
    if (status.unmigratedReviews > 0) return <WarningIcon />;
    return null;
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Site Review Structure Migration
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          Migrate site reviews from nested reviewData array to flat structure for better performance and cleaner queries.
        </Typography>

        {/* Status Overview */}
        {status && (
          <Box style={{ marginBottom: 24 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                    icon={getStatusIcon()}
                    label={status.migrationComplete ? 'Migration Complete' : 'Migration Needed'}
                    color={getStatusColor()}
                    variant={status.migrationComplete ? 'filled' : 'outlined'}
                  />
                  <Typography variant="body2">
                    {status.percentComplete}% Complete
                  </Typography>
                </Box>

                <Box style={{ marginTop: 16 }}>
                  <LinearProgress
                    variant="determinate"
                    value={status.percentComplete}
                    color={getStatusColor()}
                    style={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Total Reviews"
                      secondary={status.totalReviews}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Migrated"
                      secondary={status.migratedReviews}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Pending Migration"
                      secondary={status.unmigratedReviews}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Crew Reviews"
                      secondary={status.crewReviewCount}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>

            {/* Expandable Details */}
            {status.sampleUnmigrated && status.sampleUnmigrated.length > 0 && (
              <Box style={{ marginTop: 16 }}>
                <Button
                  onClick={() => setExpandDetails(!expandDetails)}
                  endIcon={expandDetails ? <CollapseIcon /> : <ExpandIcon />}
                  size="small"
                >
                  {expandDetails ? 'Hide' : 'Show'} Sample Unmigrated Reviews
                </Button>

                <Collapse in={expandDetails}>
                  <Box style={{ marginTop: 8, padding: 16, backgroundColor: '#fff', borderRadius: 8 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample of reviews needing migration:
                    </Typography>
                    <List dense>
                      {status.sampleUnmigrated.map((review) => (
                        <ListItem key={review._id}>
                          <ListItemText
                            primary={review.jobSite?.name || 'Unknown Site'}
                            secondary={`ID: ${review._id} | Status: ${review.status}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        )}

        {/* Test Results */}
        {testResults && (
          <Alert severity="info" style={{ marginBottom: 16 }}>
            <AlertTitle>Test Results</AlertTitle>
            <Typography variant="body2">
              New structure queries: {testResults.newQueryTime}ms |
              Old structure queries: {testResults.oldQueryTime}ms |
              {testResults.newQueryTime < testResults.oldQueryTime &&
                ` ${Math.round(((testResults.oldQueryTime - testResults.newQueryTime) / testResults.oldQueryTime) * 100)}% faster!`
              }
            </Typography>
          </Alert>
        )}

        {/* Message Display */}
        {message && (
          <Alert
            severity={message.type}
            style={{ marginBottom: 16 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box style={{ marginTop: 24, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={fetchStatus}
            disabled={loading || running}
          >
            Refresh Status
          </Button>

          <Button
            variant="outlined"
            startIcon={<TestIcon />}
            onClick={testMigration}
            disabled={loading || running}
          >
            Test Migration
          </Button>

          {status && status.unmigratedReviews > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={running ? <CircularProgress size={20} /> : <RunIcon />}
              onClick={() => handleAction('run')}
              disabled={loading || running}
            >
              Run Migration ({status.unmigratedReviews} reviews)
            </Button>
          )}

          {status && status.migratedReviews > 0 && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RollbackIcon />}
              onClick={() => handleAction('rollback')}
              disabled={loading || running}
            >
              Rollback Migration
            </Button>
          )}
        </Box>

        {/* Progress Indicator */}
        {running && (
          <Box style={{ marginTop: 16 }}>
            <LinearProgress />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Migration in progress... This may take a few minutes.
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>
          {actionType === 'run' ? 'Run Migration?' : 'Rollback Migration?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === 'run'
              ? `This will migrate ${status?.unmigratedReviews || 0} site reviews to the new structure. The process is reversible.`
              : `This will rollback ${status?.migratedReviews || 0} migrated reviews to the original structure.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmAction}
            color={actionType === 'rollback' ? 'warning' : 'primary'}
            variant="contained"
          >
            {actionType === 'run' ? 'Run Migration' : 'Confirm Rollback'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default SiteReviewMigration;